import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../components/context/AuthContext";
import {
  addGeneralLoadingSnapshot,
  addManualLoadingPhotos,
  completeLoadingControl,
  deleteLoadingControl,
  deleteShipmentCameraPhoto,
  getShipmentCameraControlDetail,
  saveLoadingControlForm,
} from "../../../api/sevkiyatCamera/SevkiyatCamApi";
import { customerApi } from "../../../utils/apiConfig";
import AmericaReportModal from "./AmericaReportModal";

function getBackendAssetUrl(fileUrl) {
  if (!fileUrl) return "";

  if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
    return fileUrl;
  }

  const baseURL = customerApi?.defaults?.baseURL || "";

  const backendOrigin = baseURL.includes("/api/")
    ? baseURL.split("/api/")[0]
    : baseURL.replace(/\/+$/, "");

  return `${backendOrigin.replace(/\/+$/, "")}${
    fileUrl.startsWith("/") ? fileUrl : `/${fileUrl}`
  }`;
}

function getPayload(response) {
  return response?.data || response;
}

function normalizeText(value = "") {
  return String(value || "").trim();
}

function getUserFullName(userValue) {
  if (!userValue || typeof userValue !== "object") return "";

  return normalizeText(
    `${userValue?.name || ""} ${userValue?.surname || ""}`
  ) || normalizeText(userValue?.email);
}

function getRecordOwnerName(record) {
  return (
    normalizeText(record?.createdByName) ||
    getUserFullName(record?.createdBy) ||
    normalizeText(record?.loadingControlForm?.generalSection?.controlUser)
  );
}

function getAuditActionLabel(action) {
  const labels = {
    CREATED: "Kayıt oluşturuldu",
    FORM_UPDATED: "Form güncellendi",
    PHOTO_ADDED: "Fotoğraf eklendi",
    PHOTO_DELETED: "Fotoğraf silindi",
    COMPLETED: "Form tamamlandı",
    DEACTIVATED: "Kayıt pasife alındı",
  };

  return labels[action] || action || "İşlem";
}

function getAuditFieldLabel(field = "") {
  const labels = {
    konteynerNo: "Konteyner No",
    firmaAdi: "Firma Adı",
    note: "Not",
    status: "Durum",
    isActive: "Aktiflik",
    photos: "Fotoğraf Sayısı",
  };

  if (labels[field]) return labels[field];

  return String(field)
    .replace(/^loadingControlForm\./, "")
    .replace(/^formHeader\./, "Üst Bilgi / ")
    .replace(/^generalSection\./, "Genel / ")
    .replace(/^productControls\./, "Ürün Kontrol / ")
    .replace(/^loadingControls\./, "Yükleme Kontrol / ")
    .replace(/^visualControls\./, "Görsel Kontrol / ")
    .replace(/\./g, " / ");
}

function formatAuditValue(value) {
  if (value === undefined || value === null || value === "") return "—";
  if (value === true) return "Evet";
  if (value === false) return "Hayır";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function getIstanbulDateTime() {
  const parts = new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const values = parts.reduce((acc, part) => {
    if (part.type !== "literal") {
      acc[part.type] = part.value;
    }

    return acc;
  }, {});

  return `${values.day}.${values.month}.${values.year} ${values.hour}:${values.minute}:${values.second}`;
}

function combineDateAndTime(dateValue, timeValue) {
  const date = normalizeText(dateValue);
  const time = normalizeText(timeValue);

  if (!date) {
    return getIstanbulDateTime();
  }

  if (/(?:T|\s)\d{1,2}:\d{2}/.test(date)) {
    return date;
  }

  return time ? `${date} ${time}` : date;
}

function isObservationSuitable(row) {
  const observation = normalizeText(row?.observation).toLocaleUpperCase(
    "tr-TR"
  );

  if (!observation) return false;
  if (observation.includes("UYGUN DEĞİL")) return false;
  if (observation.includes("UYGUN DEGIL")) return false;

  return true;
}

function isVisualSuitable(row) {
  const status = normalizeText(row?.status).toLocaleUpperCase("tr-TR");

  return status === "RESİM ALINDI" || status === "RESIM ALINDI";
}

function getSelectOptions(row) {
  if (Array.isArray(row?.options) && row.options.length > 0) {
    return row.options;
  }

  return [];
}

function shouldUseTextInput(row) {
  const options = getSelectOptions(row);

  return options.length === 0;
}

function createEmptyForm() {
  return {
    formHeader: {
      formNo: "PR07-FR24",
      revisionNo: "1",
      revisionDate: "2024-06-24",
      tarih: getIstanbulDateTime(),
      saat: "",
      ebat: "",
      malzemeCinsi: "",
      musteri: "",
      lotNo: "",
      refNo: "",
    },
    productControls: [
      {
        key: "dis_gozetim",
        parameter: "DIŞ GÖZETİM",
        tolerance: "Müşteri adına gözetmeni",
        expectedValue: "VAR - YOK",
        options: ["VAR", "YOK"],
        observation: "",
        extraFieldMode: "description",
        labelNo: "",
      },
      {
        key: "dolum_yeri",
        parameter: "DOLUM YERİ",
        tolerance: "Dolum yeri",
        expectedValue: "FABRİKA - DEPO",
        options: ["FABRİKA", "DEPO"],
        observation: "",
        extraFieldMode: "hidden",
        labelNo: "",
      },
      {
        key: "bpr_uc_hasar",
        parameter: "B/P/R UÇ KISIMLARINDA HASAR",
        tolerance: "Boru - Profil 5 cm’lik kısım dikkate alınacak",
        expectedValue: "EZİLME - VURUK - ÇAPAK",
        options: ["YOK", "EZİLME", "VURUK", "ÇAPAK"],
        observation: "",
        extraFieldMode: "hidden",
        labelNo: "",
      },
      {
        key: "bpr_yuzey_durumu",
        parameter: "B/P/R YÜZEY DURUMU",
        tolerance: "Boru - Profil tüm boy dikkate alınacak",
        expectedValue: "PAS - LEKE - TOZ - KİRLİLİK",
        options: ["YOK", "PAS", "LEKE", "TOZ", "KİRLİLİK"],
        observation: "",
        extraFieldMode: "hidden",
        labelNo: "",
      },
      {
        key: "kosebent_duzgunlugu",
        parameter: "KÖŞEBENT DÜZGÜNLÜĞÜ",
        tolerance: "Köşebent doğru noktada olup olmadığı",
        expectedValue: "UYGUN - UYGUN DEĞİL - YOK",
        options: ["UYGUN", "UYGUN DEĞİL", "YOK"],
        observation: "",
        extraFieldMode: "hidden",
        labelNo: "",
      },
      {
        key: "cember_sayisi_durumu",
        parameter: "ÇEMBER SAYISI ve DURUMU",
        tolerance:
          "1 metre ara ile 1 çember olmalı - boyasız ve paslı çember kontrolü",
        expectedValue: "UYGUN - UYGUN DEĞİL",
        options: ["UYGUN", "UYGUN DEĞİL"],
        observation: "",
        extraFieldMode: "hidden",
        labelNo: "",
      },
      {
        key: "ambalaj_durumu",
        parameter: "AMBALAJ DURUMU",
        tolerance: "Ambalaj var/yok",
        expectedValue: "VAR - YOK",
        options: ["VAR", "YOK"],
        observation: "",
        extraFieldMode: "hidden",
        labelNo: "",
      },
    ],
    loadingControls: [
      {
        key: "konteyner_tipi",
        parameter: "KONTEYNER TİPİ",
        tolerance: "",
        expectedValue: "20'' - 40''",
        options: ["20''", "40''"],
        observation: "",
        extraFieldMode: "hidden",
        containerNo: "",
      },
      {
        key: "konteyner_adet",
        parameter: "KONTEYNER ADET",
        tolerance: "Kontrol edilen konteyner sayısı",
        expectedValue: "ADET",
        options: [],
        observation: "",
        extraFieldMode: "hidden",
        containerNo: "",
      },
      {
        key: "konteyner_durumu",
        parameter: "KONTEYNER DURUMU",
        tolerance: "Çevre kontrolü, delik/yırtık durumu",
        expectedValue: "UYGUN - UYGUN DEĞİL",
        options: ["UYGUN", "UYGUN DEĞİL"],
        observation: "",
        extraFieldMode: "description",
        containerNo: "",
      },
      {
        key: "silika_jel",
        parameter: "SİLİKA JEL KULLANIMI (NEM ALICI) SAYI",
        tolerance: "Yükleme ve lashing talimatnamesine uygunluğu",
        expectedValue: "UYGUN - UYGUN DEĞİL",
        options: ["UYGUN", "UYGUN DEĞİL"],
        observation: "",
        extraFieldMode: "description",
        containerNo: "",
      },
      {
        key: "tir_adet",
        parameter: "TIR ADET",
        tolerance: "Kontrol edilen tır sayısı",
        expectedValue: "ADET",
        options: [],
        observation: "",
        extraFieldMode: "description",
        containerNo: "",
      },
      {
        key: "tir_durumu",
        parameter: "TIR DURUMU",
        tolerance: "Çevre kontrolü, delik/yırtık durumu",
        expectedValue: "UYGUN - UYGUN DEĞİL",
        options: ["UYGUN", "UYGUN DEĞİL"],
        observation: "",
        extraFieldMode: "description",
        containerNo: "",
      },
      {
        key: "ahsap_nem_olcer",
        parameter: "AHŞAP NEM ÖLÇER",
        tolerance: "Konteyner iç - orta kısmından ölçülecek",
        expectedValue: "MAX %17",
        options: [],
        observation: "",
        extraFieldMode: "description",
        containerNo: "",
      },
      {
        key: "lashing_durumu",
        parameter: "LASHINGE DURUMU",
        tolerance: "Kullanılan spanzet özellikleri",
        expectedValue: "UYGUN - UYGUN DEĞİL",
        options: ["UYGUN", "UYGUN DEĞİL"],
        observation: "",
        extraFieldMode: "description",
        containerNo: "",
      },
      {
        key: "palet_takoz_durumu",
        parameter: "PALET TAKOZ DURUMU",
        tolerance: "ISPM - IPPC",
        expectedValue: "UYGUN - UYGUN DEĞİL",
        options: ["UYGUN", "UYGUN DEĞİL"],
        observation: "",
        extraFieldMode: "description",
        containerNo: "",
      },
      {
        key: "vci_kagit_kaplama",
        parameter: "TAKOZ ÜZERİ VCI KAĞIT KAPLAMA",
        tolerance: "Takoz üzeri VCI ambalaj kağıdı ile kaplanmış mı?",
        expectedValue: "VAR - YOK",
        options: ["VAR", "YOK"],
        observation: "",
        extraFieldMode: "description",
        containerNo: "",
      },
      {
        key: "lashing_takoz_destegi",
        parameter: "LASHINGE OLARAK TAKOZ DESTEĞİ",
        tolerance: "Yükleme ve lashing talimatnamesine uygunluğu",
        expectedValue: "UYGUN - UYGUN DEĞİL",
        options: ["UYGUN", "UYGUN DEĞİL"],
        observation: "",
        extraFieldMode: "description",
        containerNo: "",
      },
    ],
    visualControls: [
      {
        key: "urun_genel_gorunum",
        parameter: "ÜRÜNLERİN GENEL GÖRÜNÜMLERİ",
        tolerance: "Aparat üzerindeki genel görüntü / min 2 görsel",
        status: "",
        note: "",
        photos: [],
      },
      {
        key: "etiket_gorseli",
        parameter: "ETİKET GÖRSELİ",
        tolerance: "Etiket net okunur şekilde / min 2 görsel",
        status: "",
        note: "",
        photos: [],
      },
      {
        key: "bos_konteyner_tir",
        parameter: "BOŞ KONTEYNER / TIR",
        tolerance: "Genel görünüm / min 2 görsel",
        status: "",
        note: "",
        photos: [],
      },
      {
        key: "dolu_konteyner_tir",
        parameter: "DOLU KONTEYNER / TIR",
        tolerance: "Genel görünüm / min 2 görsel",
        status: "",
        note: "",
        photos: [],
      },
      {
        key: "nem_alicilar",
        parameter: "NEM ALICILAR",
        tolerance: "Genel görünüm / min 2 görsel",
        status: "",
        note: "",
        photos: [],
      },
      {
        key: "lashing_son_hali",
        parameter: "LASHINGE SON HALİ",
        tolerance: "Genel görünüm / min 2 görsel",
        status: "",
        note: "",
        photos: [],
      },
      {
        key: "isil_islem_damgalari",
        parameter: "ISIL İŞLEM DAMGALARI",
        tolerance:
          "Net okunur şekilde - ön takozlarda da görülmeli / min 2 görsel",
        status: "",
        note: "",
        photos: [],
      },
      {
        key: "hasar_gorselleri",
        parameter: "HASAR VARSA GÖRSELLER",
        tolerance: "Genel görünüm / min 2 görsel",
        status: "",
        note: "",
        photos: [],
      },
      {
        key: "takoz_gorselleri",
        parameter: "TAKOZ GÖRSELLERİ",
        tolerance: "Genel görünüm / min 2 görsel",
        status: "",
        note: "",
        photos: [],
      },
    ],
    generalSection: {
      generalObservations: "",
      suitabilityLevel: "",
      totalParameterCount: 0,
      suitableParameterCount: 0,
      qualityIndex: 0,
      controlUser: "",
      approvalUser: "",
    },
  };
}

function normalizeControlRows(rows, defaultRows) {
  if (!Array.isArray(rows) || rows.length === 0) return defaultRows;

  return defaultRows.map((defaultRow) => {
    const foundRow = rows.find((item) => item?.key === defaultRow.key);

    if (!foundRow) return defaultRow;

    return {
      ...defaultRow,
      ...foundRow,
      expectedValue: defaultRow.expectedValue || foundRow.expectedValue || "",
      options: Array.isArray(defaultRow.options) ? defaultRow.options : [],
      observation: foundRow.observation || "",
      labelNo: foundRow.labelNo || "",
      containerNo: foundRow.containerNo || "",
      extraFieldMode: defaultRow.extraFieldMode || "default",
    };
  });
}

function normalizeVisualRows(rows, defaultRows) {
  if (!Array.isArray(rows) || rows.length === 0) return defaultRows;

  return defaultRows.map((defaultRow) => {
    const foundRow = rows.find((item) => item?.key === defaultRow.key);

    if (!foundRow) return defaultRow;

    return {
      ...defaultRow,
      ...foundRow,
      status: foundRow.status || "",
      note: foundRow.note || "",
      photos: Array.isArray(foundRow.photos) ? foundRow.photos : [],
    };
  });
}

function normalizeForm(form) {
  const emptyForm = createEmptyForm();
  const incomingHeader = form?.formHeader || {};

  return {
    ...emptyForm,
    ...form,
    formHeader: {
      ...emptyForm.formHeader,
      ...incomingHeader,
      tarih: combineDateAndTime(incomingHeader.tarih, incomingHeader.saat),
      saat: "",
    },
    productControls: normalizeControlRows(
      form?.productControls,
      emptyForm.productControls
    ),
    loadingControls: normalizeControlRows(
      form?.loadingControls,
      emptyForm.loadingControls
    ),
    visualControls: normalizeVisualRows(
      form?.visualControls,
      emptyForm.visualControls
    ),
    generalSection: {
      ...emptyForm.generalSection,
      ...(form?.generalSection || {}),
    },
  };
}

function calculateQuality(form) {
  const productControls = Array.isArray(form?.productControls)
    ? form.productControls
    : [];

  const loadingControls = Array.isArray(form?.loadingControls)
    ? form.loadingControls
    : [];

  const visualControls = Array.isArray(form?.visualControls)
    ? form.visualControls
    : [];

  const controlSuitableCount = [...productControls, ...loadingControls].filter(
    isObservationSuitable
  ).length;

  const visualSuitableCount = visualControls.filter(isVisualSuitable).length;

  const totalCount =
    productControls.length + loadingControls.length + visualControls.length;

  const suitableCount = controlSuitableCount + visualSuitableCount;

  const qualityIndex =
    totalCount > 0 ? Math.round((suitableCount / totalCount) * 100) : 0;

  return {
    totalParameterCount: totalCount,
    suitableParameterCount: suitableCount,
    qualityIndex,
  };
}

function getStatusLabel(status) {
  switch (status) {
    case "KANTAR_CAPTURED":
      return "Kantardan Geldi";
    case "FORM_IN_PROGRESS":
      return "Form Devam Ediyor";
    case "COMPLETED":
      return "Tamamlandı";
    case "CANCELLED":
      return "İptal";
    default:
      return status || "-";
  }
}

const responsivePageStyles = `
  .loading-control-page {
    width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .loading-control-header-main {
    min-width: 0;
  }

  .loading-control-actions {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
  }

  .camera-action-row > div:first-child {
    min-width: 0;
    flex: 1;
  }

  .responsive-table {
    width: 100%;
    min-width: 0;
  }

  @media (max-width: 992px) {
    .loading-control-header {
      flex-direction: column;
      align-items: stretch !important;
    }

    .loading-control-actions {
      width: 100%;
    }

    .loading-control-actions button {
      flex: 1;
    }
  }

  @media (max-width: 768px) {
    .loading-control-page {
      padding: 14px !important;
      border-radius: 12px !important;
      box-shadow: none !important;
    }

    .loading-control-header {
      gap: 14px !important;
      margin-bottom: 18px !important;
    }

    .page-title {
      font-size: 22px !important;
      line-height: 1.25;
    }

    .record-summary {
      line-height: 1.6;
      word-break: break-word;
    }

    .loading-control-actions {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px !important;
    }

    .loading-control-actions button,
    .camera-action-button {
      width: 100%;
      min-height: 46px;
      white-space: normal !important;
      line-height: 1.3;
    }

    .form-card {
      padding: 14px !important;
      margin-bottom: 14px !important;
      border-radius: 12px !important;
    }

    .form-card-title {
      font-size: 17px !important;
      line-height: 1.35;
      margin-bottom: 14px !important;
    }

    .camera-action-row {
      align-items: stretch !important;
      gap: 14px !important;
    }

    .camera-action-row > div:first-child {
      width: 100%;
      flex-basis: 100%;
    }

    .form-field-grid,
    .general-field-grid,
    .quality-grid {
      grid-template-columns: 1fr !important;
      gap: 12px !important;
    }

    .photo-grid {
      grid-template-columns: 1fr !important;
      gap: 12px !important;
    }

    .photo-grid-image {
      height: auto !important;
      max-height: 360px;
      aspect-ratio: 4 / 3;
    }

    .responsive-table {
      overflow: visible !important;
      border: none !important;
      border-radius: 0 !important;
    }

    .responsive-table table,
    .responsive-table tbody,
    .responsive-table tr,
    .responsive-table td {
      display: block;
      width: 100%;
      min-width: 0 !important;
      box-sizing: border-box;
    }

    .responsive-table table {
      border-collapse: separate !important;
    }

    .responsive-table thead {
      display: none;
    }

    .responsive-table tr {
      margin-bottom: 12px;
      padding: 4px 12px;
      border: 1px solid #e8e8e8;
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.035);
    }

    .responsive-table tr:last-child {
      margin-bottom: 0;
    }

    .responsive-table td {
      display: grid !important;
      grid-template-columns: minmax(112px, 38%) minmax(0, 1fr);
      align-items: start !important;
      gap: 10px;
      padding: 10px 0 !important;
      border-bottom: 1px solid #f0f0f0 !important;
      overflow-wrap: anywhere;
    }

    .responsive-table td:last-child {
      border-bottom: none !important;
    }

    .responsive-table td::before {
      content: attr(data-label);
      color: #6f7789;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.4;
    }

    .responsive-table td > * {
      min-width: 0;
      max-width: 100%;
    }

    .responsive-table input,
    .responsive-table select,
    .loading-control-page input,
    .loading-control-page select,
    .loading-control-page textarea {
      font-size: 16px !important;
    }

    .responsive-table input,
    .responsive-table select {
      min-height: 44px;
    }

    .expected-value {
      justify-self: start;
      white-space: normal !important;
    }
  }

  @media (max-width: 480px) {
    .loading-control-page {
      padding: 10px !important;
    }

    .loading-control-actions {
      grid-template-columns: 1fr;
    }

    .responsive-table td {
      grid-template-columns: 1fr;
      gap: 6px;
    }

    .responsive-table td::before {
      margin-bottom: 1px;
    }
  }
`;

function YuklemeControlDetailPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(createEmptyForm());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingAreaPhotoLoading, setLoadingAreaPhotoLoading] = useState(false);
  const [manualPhotoFiles, setManualPhotoFiles] = useState([]);
  const [manualPhotoNote, setManualPhotoNote] = useState("");
  const [manualPhotoLoading, setManualPhotoLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [deletingForm, setDeletingForm] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState("");
  const [confirmDialog, setConfirmDialog] = useState(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [americaReportOpen, setAmericaReportOpen] = useState(false);

  const qualityStats = useMemo(() => calculateQuality(form), [form]);
  const isCompleted = record?.status === "COMPLETED";
  const recordOwnerName = getRecordOwnerName(record);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      setSuccessMessage("");

      const res = await getShipmentCameraControlDetail(id);
      const payload = getPayload(res);

      if (!payload?.success) {
        throw new Error(payload?.message || "Kayıt detayı getirilemedi.");
      }

      setRecord(payload.data);
      setForm(normalizeForm(payload?.data?.loadingControlForm || {}));
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Kayıt detayı getirilirken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || !id) return;

    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  useEffect(() => {
    if (!selectedPhoto) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedPhoto(null);
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedPhoto]);

  const updateHeader = (field, value) => {
    setForm((prev) => ({
      ...prev,
      formHeader: {
        ...(prev.formHeader || {}),
        [field]: value,
      },
    }));
  };

  const updateRecordField = (field, value) => {
    setRecord((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const updateControlRow = (sectionKey, rowIndex, field, value) => {
    setForm((prev) => {
      const rows = Array.isArray(prev[sectionKey]) ? [...prev[sectionKey]] : [];

      rows[rowIndex] = {
        ...rows[rowIndex],
        [field]: value,
      };

      return {
        ...prev,
        [sectionKey]: rows,
      };
    });
  };

  const updateVisualRow = (rowIndex, field, value) => {
    setForm((prev) => {
      const rows = Array.isArray(prev.visualControls)
        ? [...prev.visualControls]
        : [];

      rows[rowIndex] = {
        ...rows[rowIndex],
        [field]: value,
      };

      return {
        ...prev,
        visualControls: rows,
      };
    });
  };

  const activateAllVisualControls = () => {
    setForm((prev) => ({
      ...prev,
      visualControls: (prev.visualControls || []).map((row) => ({
        ...row,
        status: "RESİM ALINDI",
      })),
    }));
  };

  const buildFormForSave = () => {
    return {
      ...form,
      generalSection: {
        ...(form.generalSection || {}),
        ...qualityStats,
        controlUser:
          recordOwnerName || form.generalSection?.controlUser || "",
      },
    };
  };

  const buildRecordPayload = () => ({
    konteynerNo: normalizeText(record?.konteynerNo),
    firmaAdi: normalizeText(record?.firmaAdi),
    note: normalizeText(record?.note),
  });

  const requestConfirmation = ({
    title,
    message,
    confirmLabel = "Onayla",
    tone = "danger",
  }) =>
    new Promise((resolve) => {
      setConfirmDialog({
        title,
        message,
        confirmLabel,
        tone,
        onDecision: (confirmed) => {
          setConfirmDialog(null);
          resolve(confirmed);
        },
      });
    });

  const saveCurrentForm = async () => {
    const res = await saveLoadingControlForm({
      id,
      ...buildRecordPayload(),
      loadingControlForm: buildFormForSave(),
    });

    const payload = getPayload(res);

    if (!payload?.success) {
      throw new Error(payload?.message || "Form kaydedilemedi.");
    }

    setRecord(payload.data);
    setForm(normalizeForm(payload?.data?.loadingControlForm || {}));

    return payload.data;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMessage("");
      setSuccessMessage("");

      await saveCurrentForm();

      setSuccessMessage("Yükleme kontrol formu kaydedildi.");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Form kaydedilirken hata oluştu."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleGeneralLoadingSnapshot = async () => {
    try {
      setLoadingAreaPhotoLoading(true);
      setErrorMessage("");
      setSuccessMessage("");

      await saveCurrentForm();

      const res = await addGeneralLoadingSnapshot({
        id,
      });

      const payload = getPayload(res);

      if (!payload?.success) {
        throw new Error(
          payload?.message || "Yükleme alanı fotoğrafı alınamadı."
        );
      }

      const nextRecord = payload?.data?.record || payload?.data;

      if (nextRecord) {
        setRecord(nextRecord);
        setForm(normalizeForm(nextRecord.loadingControlForm || {}));
      } else {
        await fetchDetail();
      }

      setSuccessMessage(
        "Yükleme alanı fotoğrafı sevkiyat kameralarından alındı ve kayda işlendi."
      );
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Yükleme alanı fotoğrafı alınırken hata oluştu."
      );
    } finally {
      setLoadingAreaPhotoLoading(false);
    }
  };

  const handleManualPhotoSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    setErrorMessage("");
    setSuccessMessage("");

    const existingManualCount = Array.isArray(record?.photos)
      ? record.photos.filter(
          (photo) =>
            photo?.source === "MOBILE_UPLOAD" ||
            photo?.source === "MANUAL_UPLOAD" ||
            photo?.photoType === "SEVKIYAT_MANUEL"
        ).length
      : 0;

    const remainingCount = Math.max(30 - existingManualCount, 0);

    if (remainingCount === 0) {
      setManualPhotoFiles([]);
      event.target.value = "";
      setErrorMessage(
        "Bu kayıt için 30 adet manuel fotoğraf sınırına ulaşıldı."
      );
      return;
    }

    if (selectedFiles.length > remainingCount) {
      setManualPhotoFiles(selectedFiles.slice(0, remainingCount));
      setErrorMessage(
        `Bu kayda en fazla ${remainingCount} fotoğraf daha ekleyebilirsin. İlk ${remainingCount} fotoğraf seçildi.`
      );
      return;
    }

    setManualPhotoFiles(selectedFiles);
  };

  const handleManualPhotoUpload = async () => {
    if (manualPhotoFiles.length === 0) {
      setErrorMessage("Yüklemek için en az bir fotoğraf seçmelisin.");
      return;
    }

    try {
      setManualPhotoLoading(true);
      setErrorMessage("");
      setSuccessMessage("");

      await saveCurrentForm();

      const res = await addManualLoadingPhotos({
        id,
        files: manualPhotoFiles,
        note: manualPhotoNote,
      });

      const payload = getPayload(res);

      if (!payload?.success) {
        throw new Error(payload?.message || "Manuel fotoğraflar yüklenemedi.");
      }

      const nextRecord = payload?.data?.record || payload?.data;

      if (nextRecord) {
        setRecord(nextRecord);
        setForm(normalizeForm(nextRecord.loadingControlForm || {}));
      } else {
        await fetchDetail();
      }

      setManualPhotoFiles([]);
      setManualPhotoNote("");
      setSuccessMessage(
        `${
          payload?.data?.uploadedPhotos?.length || manualPhotoFiles.length
        } adet manuel fotoğraf başarıyla yüklendi.`
      );
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Manuel fotoğraflar yüklenirken hata oluştu."
      );
    } finally {
      setManualPhotoLoading(false);
    }
  };

  const handleComplete = async () => {
    const confirmed = await requestConfirmation({
      title: "Form Tamamlansın mı?",
      message:
        "Bu form tamamlandı olarak işaretlenecek. İşlemden sonra kayıt tamamlanmış duruma geçecek.",
      confirmLabel: "Formu Tamamla",
      tone: "success",
    });

    if (!confirmed) return;

    try {
      setCompleting(true);
      setErrorMessage("");
      setSuccessMessage("");

      const res = await completeLoadingControl({
        id,
        ...buildRecordPayload(),
        loadingControlForm: buildFormForSave(),
      });

      const payload = getPayload(res);

      if (!payload?.success) {
        throw new Error(payload?.message || "Form tamamlanamadı.");
      }

      setRecord(payload.data);
      setForm(normalizeForm(payload?.data?.loadingControlForm || {}));
      setSuccessMessage("Yükleme kontrol formu tamamlandı.");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Form tamamlanırken hata oluştu."
      );
    } finally {
      setCompleting(false);
    }
  };

  const handleDeleteForm = async () => {
    const confirmed = await requestConfirmation({
      title: "Form Pasife Alınsın mı?",
      message:
        "Bu form pasife alınacak ve listede görünmeyecek. İstersen daha sonra tekrar yükleyebilirsin.",
      confirmLabel: "Pasife Al",
      tone: "danger",
    });

    if (!confirmed) return;

    try {
      setDeletingForm(true);
      setErrorMessage("");
      setSuccessMessage("");

      const res = await deleteLoadingControl({ id });
      const payload = getPayload(res);

      if (!payload?.success) {
        throw new Error(payload?.message || "Form pasife alınamadı.");
      }

      navigate("/sevkiyat/yukleme-kontrol");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Form pasife alınırken hata oluştu."
      );
    } finally {
      setDeletingForm(false);
    }
  };

  const handleDeletePhoto = async (photo) => {
    const photoId = photo?._id;

    if (!photoId) {
      setErrorMessage("Silinecek fotoğraf bulunamadı.");
      return;
    }

    const confirmed = await requestConfirmation({
      title: "Fotoğraf Silinsin mi?",
      message:
        "Bu fotoğraf kayıttan kalıcı olarak silinecek. Silinen fotoğraf geri alınamaz.",
      confirmLabel: "Fotoğrafı Sil",
      tone: "danger",
    });

    if (!confirmed) return;

    try {
      setDeletingPhotoId(String(photoId));
      setErrorMessage("");
      setSuccessMessage("");

      const res = await deleteShipmentCameraPhoto({
        id,
        photoId: String(photoId),
      });
      const payload = getPayload(res);

      if (!payload?.success) {
        throw new Error(payload?.message || "Fotoğraf silinemedi.");
      }

      const nextRecord = payload?.data;

      if (nextRecord) {
        setRecord(nextRecord);
        setForm(normalizeForm(nextRecord.loadingControlForm || {}));
      } else {
        await fetchDetail();
      }

      setSelectedPhoto((current) =>
        String(current?._id || "") === String(photoId) ? null : current
      );
      setSuccessMessage("Fotoğraf silindi.");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Fotoğraf silinirken hata oluştu."
      );
    } finally {
      setDeletingPhotoId("");
    }
  };

  if (!user) return null;

  const photos = Array.isArray(record?.photos) ? record.photos : [];

  const kantarPhotos = photos.filter(
    (photo) => photo.photoType === "KANTAR_GIRIS"
  );

  const loadingAreaPhotos = photos.filter(
    (photo) =>
      photo.photoType === "SEVKIYAT_KONTROL" &&
      (!photo.visualKey || photo.visualKey === "YUKLEME_ALANI_GENEL")
  );

  const manualPhotos = photos.filter(
    (photo) =>
      photo?.source === "MOBILE_UPLOAD" ||
      photo?.source === "MANUAL_UPLOAD" ||
      photo?.photoType === "SEVKIYAT_MANUEL"
  );

  const remainingManualPhotoCount = Math.max(30 - manualPhotos.length, 0);

  // Amerika raporu modalı bu tek kaydı da bir "konteyner listesi" olarak
  // alır; loadingControlForm'u ekrandaki (henüz kaydedilmemiş olabilecek)
  // canlı form state'iyle değiştiriyoruz ki rapor güncel veriyi yansıtsın.
  const americaReportRecords = record
    ? [{ ...record, loadingControlForm: form }]
    : [];

  return (
    <>
      <style>{responsivePageStyles}</style>

      <section
        className="loading-control-page"
        style={{
          flex: 1,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          padding: "clamp(14px, 3vw, 32px)",
          minHeight: 420,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div
          className="loading-control-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 24,
          }}
        >
          <div className="loading-control-header-main">
            <button
              type="button"
              onClick={() => navigate("/sevkiyat/yukleme-kontrol")}
              style={{
                ...secondaryButtonStyle,
                marginBottom: 12,
              }}
            >
              Listeye Dön
            </button>

            <h2
              className="page-title"
              style={{
                fontWeight: 600,
                color: "#283049",
                marginBottom: 8,
                fontSize: 26,
              }}
            >
              Yükleme Kontrol Formu
            </h2>

            <p
              className="record-summary"
              style={{
                color: "#6c757d",
                fontSize: 15,
                margin: 0,
                maxWidth: 720,
              }}
            >
              Konteyner No: <strong>{record?.konteynerNo || "-"}</strong> /
              Firma: <strong>{record?.firmaAdi || "-"}</strong> / Durum:{" "}
              <strong>{getStatusLabel(record?.status)}</strong>
            </p>
          </div>

          <FormActionButtons
            loading={loading}
            saving={saving}
            completing={completing}
            deleting={deletingForm}
            isCompleted={isCompleted}
            onSave={handleSave}
            onComplete={handleComplete}
            onDelete={handleDeleteForm}
            onAmericaReport={() => setAmericaReportOpen(true)}
          />
        </div>

        {errorMessage && <div style={errorBoxStyle}>{errorMessage}</div>}

        {successMessage && <div style={successBoxStyle}>{successMessage}</div>}

        {loading ? (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "#8c8c8c",
            }}
          >
            Kayıt yükleniyor...
          </div>
        ) : (
          <>
            <FormCard title="Kantar Fotoğrafları">
              {kantarPhotos.length === 0 ? (
                <div style={{ color: "#8c8c8c", fontSize: 14 }}>
                  Kantar fotoğrafı bulunamadı.
                </div>
              ) : (
                <PhotoGrid
                  photos={kantarPhotos}
                  onPhotoClick={setSelectedPhoto}
                  onDeletePhoto={handleDeletePhoto}
                  deletingPhotoId={deletingPhotoId}
                />
              )}
            </FormCard>

            <FormCard title="Yükleme Alanı Kamera Fotoğrafları">
              <div
                className="camera-action-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 16,
                  marginBottom: 18,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div
                    style={{
                      color: "#283049",
                      fontSize: 17,
                      fontWeight: 700,
                      marginBottom: 5,
                    }}
                  >
                    Sevkiyat kameralarından yükleme alanı fotoğrafı al
                  </div>

                  <div
                    style={{
                      color: "#8c8c8c",
                      fontSize: 14,
                    }}
                  >
                    Bu fotoğraflar alttaki görsel kontrol satırlarından bağımsız
                    olarak doğrudan sevkiyat kaydına eklenir.
                  </div>
                </div>

                <button
                  className="camera-action-button"
                  type="button"
                  disabled={isCompleted || loadingAreaPhotoLoading}
                  onClick={handleGeneralLoadingSnapshot}
                  style={{
                    ...primaryButtonStyle,
                    minHeight: 54,
                    fontSize: 16,
                    padding: "14px 24px",
                    background:
                      isCompleted || loadingAreaPhotoLoading
                        ? "#bfbfbf"
                        : "#1677ff",
                    cursor:
                      isCompleted || loadingAreaPhotoLoading
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {loadingAreaPhotoLoading
                    ? "Yükleme Alanı Fotoğrafı Çekiliyor..."
                    : "Yükleme Alanı Fotoğraf Çek ve Kaydet"}
                </button>
              </div>

              {loadingAreaPhotos.length === 0 ? (
                <div style={{ color: "#8c8c8c", fontSize: 14 }}>
                  Henüz yükleme alanı fotoğrafı alınmadı.
                </div>
              ) : (
                <PhotoGrid
                  photos={loadingAreaPhotos}
                  onPhotoClick={setSelectedPhoto}
                  onDeletePhoto={handleDeletePhoto}
                  deletingPhotoId={deletingPhotoId}
                />
              )}
            </FormCard>

            <FormCard title="Telefon Üzerinden Manuel Fotoğraf Yükleme">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    background: "#f6f8fb",
                    border: "1px solid #e6ebf2",
                    borderRadius: 12,
                    padding: 14,
                    color: "#595959",
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}
                >
                  Telefon galerisinden birden fazla fotoğraf seçebilirsin. Kayıt
                  başına en fazla <strong>30 fotoğraf</strong> yüklenebilir.
                  Kalan hak: <strong>{remainingManualPhotoCount}</strong>
                </div>
                <div>
                  <label style={labelStyle}>Fotoğraf Açıklaması</label>

                  <input
                    value={manualPhotoNote}
                    disabled={isCompleted || manualPhotoLoading}
                    onChange={(event) => setManualPhotoNote(event.target.value)}
                    placeholder="Örn: Yükleme tamamlandıktan sonra çekildi"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Galeriden Fotoğraf Seç</label>

                  <input
                    key={`${manualPhotoFiles.length}-${manualPhotos.length}`}
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={
                      isCompleted ||
                      manualPhotoLoading ||
                      remainingManualPhotoCount === 0
                    }
                    onChange={handleManualPhotoSelect}
                    style={{
                      ...inputStyle,
                      height: "auto",
                      minHeight: 48,
                      padding: 10,
                      background:
                        isCompleted || remainingManualPhotoCount === 0
                          ? "#f5f5f5"
                          : "#fff",
                    }}
                  />

                  <div
                    style={{
                      marginTop: 7,
                      color: "#8c8c8c",
                      fontSize: 12,
                      lineHeight: 1.5,
                    }}
                  >
                    JPG, PNG, WEBP, HEIC ve HEIF fotoğrafları seçebilirsin.
                  </div>
                </div>
                {manualPhotoFiles.length > 0 && (
                  <div
                    style={{
                      border: "1px solid #d9d9d9",
                      borderRadius: 10,
                      padding: 12,
                      background: "#fafafa",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#283049",
                        marginBottom: 8,
                      }}
                    >
                      Seçilen fotoğraf sayısı: {manualPhotoFiles.length}
                    </div>

                    {manualPhotoFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${file.lastModified}-${index}`}
                        style={{
                          color: "#6c757d",
                          fontSize: 13,
                          padding: "3px 0",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {index + 1}. {file.name}
                      </div>
                    ))}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={handleManualPhotoUpload}
                    disabled={
                      isCompleted ||
                      manualPhotoLoading ||
                      manualPhotoFiles.length === 0 ||
                      remainingManualPhotoCount === 0
                    }
                    style={{
                      ...primaryButtonStyle,
                      minHeight: 46,
                      background:
                        isCompleted ||
                        manualPhotoLoading ||
                        manualPhotoFiles.length === 0 ||
                        remainingManualPhotoCount === 0
                          ? "#bfbfbf"
                          : "#722ed1",
                      cursor:
                        isCompleted ||
                        manualPhotoLoading ||
                        manualPhotoFiles.length === 0 ||
                        remainingManualPhotoCount === 0
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    {manualPhotoLoading
                      ? "Fotoğraflar Yükleniyor..."
                      : `${manualPhotoFiles.length || 0} Fotoğrafı Yükle`}
                  </button>

                  {manualPhotoFiles.length > 0 && (
                    <button
                      type="button"
                      disabled={manualPhotoLoading}
                      onClick={() => setManualPhotoFiles([])}
                      style={secondaryButtonStyle}
                    >
                      Seçimi Temizle
                    </button>
                  )}
                </div>
                {manualPhotos.length === 0 ? (
                  <div style={{ color: "#8c8c8c", fontSize: 14 }}>
                    Henüz telefon üzerinden manuel fotoğraf yüklenmedi.
                  </div>
                ) : (
                  <PhotoGrid
                    photos={manualPhotos}
                    onPhotoClick={setSelectedPhoto}
                    onDeletePhoto={handleDeletePhoto}
                    deletingPhotoId={deletingPhotoId}
                  />
                )}
              </div>
            </FormCard>

            <FormCard title="Form Üst Bilgileri">
              <div
                className="form-field-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                <Field
                  label="Konteyner No"
                  value={record?.konteynerNo}
                  disabled={isCompleted}
                  onChange={(value) => updateRecordField("konteynerNo", value)}
                />

                <Field
                  label="Firma Adı"
                  value={record?.firmaAdi}
                  disabled={isCompleted}
                  onChange={(value) => updateRecordField("firmaAdi", value)}
                />

                <Field
                  label="İrsaliye No"
                  value={record?.irsaliyeNo}
                  disabled
                  onChange={() => {}}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Not</label>

                <textarea
                  value={record?.note || ""}
                  disabled={isCompleted}
                  onChange={(event) =>
                    updateRecordField("note", event.target.value)
                  }
                  rows={3}
                  placeholder="Sevkiyat notu"
                  style={textareaStyle}
                />
              </div>

              <div
                className="form-field-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 14,
                }}
              >
                <Field
                  label="Form No"
                  value={form.formHeader.formNo}
                  disabled
                  onChange={(value) => updateHeader("formNo", value)}
                />

                <Field
                  label="Revizyon No"
                  value={form.formHeader.revisionNo}
                  disabled
                  onChange={(value) => updateHeader("revisionNo", value)}
                />

                <Field
                  label="Revizyon Tarihi"
                  value={form.formHeader.revisionDate}
                  disabled
                  onChange={(value) => updateHeader("revisionDate", value)}
                />

                <Field
                  label="Tarih ve Saat"
                  value={form.formHeader.tarih}
                  disabled={isCompleted}
                  onChange={(value) => updateHeader("tarih", value)}
                />

                <Field
                  label="Ebat"
                  value={form.formHeader.ebat}
                  disabled={isCompleted}
                  onChange={(value) => updateHeader("ebat", value)}
                />

                <Field
                  label="Malzeme Cinsi"
                  value={form.formHeader.malzemeCinsi}
                  disabled={isCompleted}
                  onChange={(value) => updateHeader("malzemeCinsi", value)}
                />

                <Field
                  label="Müşteri"
                  value={form.formHeader.musteri}
                  disabled={isCompleted}
                  onChange={(value) => updateHeader("musteri", value)}
                />

                <Field
                  label="Lot No"
                  value={form.formHeader.lotNo}
                  disabled={isCompleted}
                  onChange={(value) => updateHeader("lotNo", value)}
                />

                <Field
                  label="Ref No"
                  value={form.formHeader.refNo}
                  disabled={isCompleted}
                  onChange={(value) => updateHeader("refNo", value)}
                />

                <Field
                  label="Kayıt Sahibi / Kontrol Eden"
                  value={recordOwnerName || "-"}
                  disabled
                  onChange={() => {}}
                />
              </div>
            </FormCard>

            <FormCard title="Kontrol Edilecek Parametreler - Ürün">
              <ControlTable
                rows={form.productControls}
                sectionKey="productControls"
                extraColumnLabel="Açıklama"
                extraField="labelNo"
                disabled={isCompleted}
                onChange={updateControlRow}
              />
            </FormCard>

            <FormCard title="Kontrol Edilecek Parametreler - Yükleme">
              <ControlTable
                rows={form.loadingControls}
                sectionKey="loadingControls"
                extraColumnLabel="Açıklama"
                extraField="containerNo"
                disabled={isCompleted}
                onChange={updateControlRow}
              />
            </FormCard>

            <FormCard title="Görüntü Alınacak Bilgiler">
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 14,
                }}
              >
                <button
                  type="button"
                  disabled={isCompleted}
                  onClick={activateAllVisualControls}
                  style={{
                    ...primaryButtonStyle,
                    background: isCompleted ? "#bfbfbf" : "#1677ff",
                    cursor: isCompleted ? "not-allowed" : "pointer",
                  }}
                >
                  Hepsini Aktifleştir
                </button>
              </div>

              <div
                className="responsive-table"
                style={{
                  overflowX: "auto",
                  border: "1px solid #f0f0f0",
                  borderRadius: 14,
                }}
              >
                <table
                  style={{
                    width: "100%",
                    minWidth: 700,
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#fafafa" }}>
                      <th style={thStyle}>Parametre</th>
                      <th style={thStyle}>Açıklama / Limit</th>
                      <th style={thStyle}>Resim Alındı</th>
                    </tr>
                  </thead>

                  <tbody>
                    {form.visualControls.map((row, index) => {
                      const checked = isVisualSuitable(row);

                      return (
                        <tr key={row.key}>
                          <td data-label="Parametre" style={tdStyle}>
                            <strong>{row.parameter}</strong>
                          </td>

                          <td data-label="Açıklama / Limit" style={tdStyle}>
                            {row.tolerance}
                          </td>

                          <td data-label="Resim Alındı" style={tdStyle}>
                            <label
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 10,
                                minHeight: 38,
                                cursor: isCompleted ? "not-allowed" : "pointer",
                                fontWeight: 600,
                                color: checked ? "#237804" : "#8c8c8c",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={isCompleted}
                                onChange={(event) =>
                                  updateVisualRow(
                                    index,
                                    "status",
                                    event.target.checked
                                      ? "RESİM ALINDI"
                                      : "RESİM ALINMADI"
                                  )
                                }
                                style={{
                                  width: 20,
                                  height: 20,
                                  cursor: isCompleted
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              />
                              {checked ? "RESİM ALINDI" : "RESİM ALINMADI"}
                            </label>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </FormCard>

            <AuditHistory logs={record?.auditLogs} />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                paddingTop: 4,
              }}
            >
              <FormActionButtons
                loading={loading}
                saving={saving}
                completing={completing}
                deleting={deletingForm}
                isCompleted={isCompleted}
                onSave={handleSave}
                onComplete={handleComplete}
                onDelete={handleDeleteForm}
                onAmericaReport={() => setAmericaReportOpen(true)}
              />
            </div>

            <button
              type="button"
              onClick={() => navigate("/sevkiyat/yukleme-kontrol")}
              style={{
                ...secondaryButtonStyle,
                width: "100%",
                minHeight: 48,
                marginTop: 14,
                marginBottom: 8,
                fontSize: 15,
              }}
            >
              Listeye Dön
            </button>
          </>
        )}

        {selectedPhoto && (
          <PhotoLightbox
            photo={selectedPhoto}
            onClose={() => setSelectedPhoto(null)}
          />
        )}

        <AmericaReportModal
          open={americaReportOpen}
          onClose={() => setAmericaReportOpen(false)}
          records={americaReportRecords}
        />

        {confirmDialog && (
          <ConfirmationDialog
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            tone={confirmDialog.tone}
            onCancel={() => confirmDialog.onDecision(false)}
            onConfirm={() => confirmDialog.onDecision(true)}
          />
        )}
      </section>
    </>
  );
}

function AuditHistory({ logs }) {
  const orderedLogs = [...(Array.isArray(logs) ? logs : [])].sort(
    (left, right) =>
      new Date(right?.occurredAt || 0).getTime() -
      new Date(left?.occurredAt || 0).getTime()
  );

  return (
    <FormCard title="İşlem Geçmişi">
      {orderedLogs.length === 0 ? (
        <div style={{ color: "#8c8c8c", fontSize: 14 }}>
          Bu kayıt için henüz işlem geçmişi bulunmuyor.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {orderedLogs.map((log, index) => (
            <div
              key={log?._id || `${log?.occurredAt}-${index}`}
              style={{
                border: "1px solid #e8edf3",
                borderRadius: 12,
                padding: 14,
                background: "#fafcff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div style={{ color: "#283049", fontWeight: 700 }}>
                    {getAuditActionLabel(log?.action)}
                  </div>
                  <div style={{ color: "#595959", fontSize: 13, marginTop: 4 }}>
                    {log?.description || "-"}
                  </div>
                </div>

                <div style={{ color: "#6f7789", fontSize: 13, textAlign: "right" }}>
                  <strong>{log?.actorName || "Bilinmeyen Kullanıcı"}</strong>
                  <div>
                    {log?.occurredAt
                      ? new Date(log.occurredAt).toLocaleString("tr-TR")
                      : "-"}
                  </div>
                </div>
              </div>

              {Array.isArray(log?.changes) && log.changes.length > 0 && (
                <details style={{ marginTop: 10 }}>
                  <summary
                    style={{ color: "#1677ff", cursor: "pointer", fontSize: 13 }}
                  >
                    Değişiklikleri göster ({log.changes.length})
                  </summary>

                  <div style={{ display: "grid", gap: 7, marginTop: 10 }}>
                    {log.changes.map((change, changeIndex) => (
                      <div
                        key={`${change?.field}-${changeIndex}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: 10,
                          fontSize: 12,
                          overflowWrap: "anywhere",
                        }}
                      >
                        <strong>{getAuditFieldLabel(change?.field)}</strong>
                        <span style={{ color: "#a8071a" }}>
                          Önce: {formatAuditValue(change?.oldValue)}
                        </span>
                        <span style={{ color: "#237804" }}>
                          Sonra: {formatAuditValue(change?.newValue)}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </FormCard>
  );
}

function FormActionButtons({
  loading,
  saving,
  completing,
  deleting,
  isCompleted,
  onSave,
  onComplete,
  onDelete,
  onAmericaReport,
}) {
  return (
    <div className="loading-control-actions">
      {onAmericaReport && (
        <button
          type="button"
          onClick={onAmericaReport}
          disabled={loading}
          style={{
            ...primaryButtonStyle,
            background: loading ? "#bfbfbf" : "#722ed1",
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          Amerika Raporu Al
        </button>
      )}

      <button
        type="button"
        onClick={onSave}
        disabled={saving || loading || isCompleted}
        style={{
          ...primaryButtonStyle,
          background: saving || isCompleted ? "#bfbfbf" : "#1677ff",
          cursor: saving || isCompleted ? "not-allowed" : "pointer",
        }}
      >
        {saving ? "Kaydediliyor..." : "Formu Kaydet"}
      </button>

      <button
        type="button"
        onClick={onComplete}
        disabled={completing || loading || isCompleted}
        style={{
          ...primaryButtonStyle,
          background: completing || isCompleted ? "#bfbfbf" : "#52c41a",
          cursor: completing || isCompleted ? "not-allowed" : "pointer",
        }}
      >
        {isCompleted
          ? "Tamamlandı"
          : completing
            ? "Tamamlanıyor..."
            : "Formu Tamamla"}
      </button>

      <button
        type="button"
        onClick={onDelete}
        disabled={deleting || loading}
        style={{
          ...secondaryButtonStyle,
          borderColor: "#ffccc7",
          color: "#cf1322",
          background: deleting || loading ? "#fff1f0" : "#fff",
          cursor: deleting || loading ? "not-allowed" : "pointer",
          opacity: deleting || loading ? 0.7 : 1,
        }}
      >
        {deleting ? "Pasife Alınıyor..." : "Formu Pasife Al"}
      </button>
    </div>
  );
}

function FormCard({ title, children }) {
  return (
    <div
      className="form-card"
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 14,
        padding: 18,
        marginBottom: 20,
        background: "#fff",
      }}
    >
      <h3
        className="form-card-title"
        style={{
          fontSize: 19,
          color: "#283049",
          marginTop: 0,
          marginBottom: 16,
          fontWeight: 600,
        }}
      >
        {title}
      </h3>

      {children}
    </div>
  );
}

function Field({ label, value, onChange, disabled }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>

      <input
        value={value || ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...inputStyle,
          background: disabled ? "#f5f5f5" : "#fff",
        }}
      />
    </div>
  );
}

function ConfirmationDialog({
  title,
  message,
  confirmLabel,
  tone,
  onCancel,
  onConfirm,
}) {
  const confirmStyle =
    tone === "success"
      ? {
          background: "#52c41a",
          color: "#fff",
          border: "none",
        }
      : {
          background: "#cf1322",
          color: "#fff",
          border: "none",
        };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        background: "rgba(15, 23, 42, 0.54)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          padding: 20,
        }}
      >
        <div
          style={{
            color: "#283049",
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          {title}
        </div>

        <div
          style={{
            color: "#595959",
            fontSize: 15,
            lineHeight: 1.6,
            marginBottom: 18,
          }}
        >
          {message}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
            Vazgeç
          </button>

          <button
            type="button"
            onClick={onConfirm}
            style={{
              ...primaryButtonStyle,
              ...confirmStyle,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotoGrid({ photos, onPhotoClick, onDeletePhoto, deletingPhotoId }) {
  const openPhoto = (photo) => {
    if (typeof onPhotoClick === "function") {
      onPhotoClick(photo);
    }
  };

  return (
    <div
      className="photo-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 14,
      }}
    >
      {photos.map((photo) => (
        <div
          key={photo._id || photo.fileUrl}
          style={{
            position: "relative",
            border: "1px solid #f0f0f0",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              if (typeof onDeletePhoto === "function") {
                onDeletePhoto(photo);
              }
            }}
            disabled={
              !photo?._id || String(deletingPhotoId || "") === String(photo?._id)
            }
            aria-label="Fotoğrafı sil"
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 2,
              width: 34,
              height: 34,
              border: "1px solid rgba(255,255,255,0.7)",
              borderRadius: "50%",
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              fontSize: 20,
              lineHeight: 1,
              cursor:
                !photo?._id || String(deletingPhotoId || "") === String(photo?._id)
                  ? "not-allowed"
                  : "pointer",
              opacity:
                !photo?._id || String(deletingPhotoId || "") === String(photo?._id)
                  ? 0.65
                  : 1,
            }}
          >
            ×
          </button>

          <img
            className="photo-grid-image"
            src={getBackendAssetUrl(photo.fileUrl)}
            alt={photo.cameraLabel || photo.cameraKey || "Sevkiyat fotoğrafı"}
            role="button"
            tabIndex={0}
            onClick={() => openPhoto(photo)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openPhoto(photo);
              }
            }}
            title="Fotoğrafı büyüt"
            style={{
              width: "100%",
              height: 220,
              objectFit: "cover",
              display: "block",
              background: "#f5f5f5",
              cursor: "zoom-in",
            }}
          />

          <div style={{ padding: 12 }}>
            <div
              style={{
                color: "#283049",
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              {photo.cameraLabel || photo.cameraKey || "Fotoğraf"}
            </div>

            <div
              style={{
                color: "#8c8c8c",
                fontSize: 13,
              }}
            >
              {photo.capturedAt
                ? new Date(photo.capturedAt).toLocaleString("tr-TR")
                : ""}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PhotoLightbox({ photo, onClose }) {
  const photoUrl = getBackendAssetUrl(photo?.fileUrl);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Büyütülmüş fotoğraf"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "rgba(0, 0, 0, 0.86)",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Fotoğrafı kapat"
        style={{
          position: "fixed",
          top: 18,
          right: 18,
          width: 44,
          height: 44,
          border: "1px solid rgba(255, 255, 255, 0.45)",
          borderRadius: "50%",
          background: "rgba(0, 0, 0, 0.45)",
          color: "#fff",
          fontSize: 28,
          lineHeight: 1,
          cursor: "pointer",
          zIndex: 10000,
        }}
      >
        ×
      </button>

      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          maxWidth: "96vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <img
          src={photoUrl}
          alt={photo?.cameraLabel || photo?.cameraKey || "Sevkiyat fotoğrafı"}
          style={{
            display: "block",
            maxWidth: "96vw",
            maxHeight: "84vh",
            objectFit: "contain",
            borderRadius: 10,
            background: "#111",
          }}
        />

        <div
          style={{
            color: "#fff",
            textAlign: "center",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <strong>
            {photo?.cameraLabel || photo?.cameraKey || "Fotoğraf"}
          </strong>

          {photo?.capturedAt && (
            <div style={{ opacity: 0.78 }}>
              {new Date(photo.capturedAt).toLocaleString("tr-TR")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ControlTable({
  rows,
  sectionKey,
  extraColumnLabel,
  extraField,
  disabled,
  onChange,
}) {
  return (
    <div
      className="responsive-table"
      style={{
        overflowX: "auto",
        border: "1px solid #f0f0f0",
        borderRadius: 14,
      }}
    >
      <table
        style={{
          width: "100%",
          minWidth: 1050,
          borderCollapse: "collapse",
        }}
      >
        <thead>
          <tr style={{ background: "#fafafa" }}>
            <th style={thStyle}>Parametre</th>
            <th style={thStyle}>Açıklama / Tolerans Limitleri</th>
            <th style={thStyle}>Parametrenin Ölçülen Değeri</th>
            <th style={thStyle}>Gözlemler / Açıklamalar</th>
            <th style={thStyle}>{extraColumnLabel}</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const options = getSelectOptions(row);
            const useTextInput = shouldUseTextInput(row);
            const extraFieldMode = row.extraFieldMode || "default";
            const showExtraField = extraFieldMode !== "hidden";
            const rowExtraFieldLabel =
              extraFieldMode === "description"
                ? "Açıklama"
                : extraColumnLabel;

            return (
              <tr key={row.key}>
                <td data-label="Parametre" style={tdStyle}>
                  <strong>{row.parameter}</strong>
                </td>

                <td data-label="Açıklama / Tolerans" style={tdStyle}>
                  {row.tolerance || "-"}
                </td>

                <td data-label="Ölçülen Değer" style={tdStyle}>
                  <span
                    className="expected-value"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      minHeight: 30,
                      padding: "5px 9px",
                      borderRadius: 8,
                      background: "#f5f5f5",
                      color: "#595959",
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {row.expectedValue || "-"}
                  </span>
                </td>

                <td data-label="Gözlem / Açıklama" style={tdStyle}>
                  {useTextInput ? (
                    <input
                      value={row.observation || ""}
                      disabled={disabled}
                      placeholder={row.expectedValue || "Gözlem / açıklama"}
                      onChange={(e) =>
                        onChange(
                          sectionKey,
                          index,
                          "observation",
                          e.target.value
                        )
                      }
                      style={smallInputStyle}
                    />
                  ) : (
                    <select
                      value={row.observation || ""}
                      disabled={disabled}
                      onChange={(e) =>
                        onChange(
                          sectionKey,
                          index,
                          "observation",
                          e.target.value
                        )
                      }
                      style={smallInputStyle}
                    >
                      <option value="">Seçiniz</option>

                      {options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                </td>

                <td
                  data-label={showExtraField ? rowExtraFieldLabel : ""}
                  style={tdStyle}
                >
                  {showExtraField ? (
                    <input
                      value={row[extraField] || ""}
                      disabled={disabled}
                      placeholder={rowExtraFieldLabel}
                      aria-label={`${row.parameter} - ${rowExtraFieldLabel}`}
                      onChange={(e) =>
                        onChange(sectionKey, index, extraField, e.target.value)
                      }
                      style={smallInputStyle}
                    />
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const labelStyle = {
  display: "block",
  color: "#283049",
  fontWeight: 600,
  marginBottom: 7,
  fontSize: 13,
};

const inputStyle = {
  width: "100%",
  height: 44,
  border: "1px solid #d9d9d9",
  borderRadius: 10,
  padding: "0 12px",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const smallInputStyle = {
  width: "100%",
  height: 38,
  border: "1px solid #d9d9d9",
  borderRadius: 8,
  padding: "0 10px",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
};

const textareaStyle = {
  width: "100%",
  border: "1px solid #d9d9d9",
  borderRadius: 10,
  padding: 12,
  fontSize: 14,
  resize: "vertical",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const primaryButtonStyle = {
  border: "none",
  background: "#1677ff",
  color: "#fff",
  fontWeight: 600,
  borderRadius: 10,
  padding: "10px 15px",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const secondaryButtonStyle = {
  border: "1px solid #d9d9d9",
  background: "#fff",
  color: "#283049",
  fontWeight: 600,
  borderRadius: 10,
  padding: "9px 13px",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const thStyle = {
  padding: "12px 10px",
  textAlign: "left",
  color: "#595959",
  fontSize: 13,
  fontWeight: 700,
  borderBottom: "1px solid #f0f0f0",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px 10px",
  color: "#283049",
  fontSize: 14,
  borderBottom: "1px solid #f5f5f5",
  verticalAlign: "middle",
};

const errorBoxStyle = {
  background: "#fff1f0",
  border: "1px solid #ffccc7",
  color: "#a8071a",
  padding: "12px 14px",
  borderRadius: 10,
  marginBottom: 18,
  fontSize: 14,
};

const successBoxStyle = {
  background: "#f6ffed",
  border: "1px solid #b7eb8f",
  color: "#237804",
  padding: "12px 14px",
  borderRadius: 10,
  marginBottom: 18,
  fontSize: 14,
};

export default YuklemeControlDetailPage;
